import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  SectionCard,
  Divider,
  Tile,
  PrimaryButton,
  SecondaryButton,
  GhostButton
} from "@/components/ds";
import { Lightbulb } from "@/components/ds/icons";
import useApi from "@/hooks/useApi";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import IntegrationNotEnabled from "../shared/IntegrationNotEnabled";
import CloverDataMapping from "./CloverDataMapping";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";

const CLOVER_COLOR = "#1DA462";

// ─── Exported nav helpers ──────────────────────────────────────────────────────

export function BackToIntegrationLandingClover() {
  const navigate = useNavigate();
  return (
    <GhostButton
      onClick={() => navigate("/integrations")}
      className="!text-zinc-600 hover:!text-zinc-800 hover:!no-underline !gap-2"
    >
      <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
          clipRule="evenodd"
        />
      </svg>
      Back to Integrations
    </GhostButton>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface CloverConfig {
  id: number;
  siteId: number;
  displayName: string;
  merchantId: string;
  locationName: string;
  enabled: boolean;
  createdAt: string;
  accessTokenMasked: string | null;
  authMethod: string;
  oauthConnectedAt: string | null;
}

type TabId = "overview" | "accounts" | "ai-tools" | "data-mapping" | "logs";

// ─── Capabilities ─────────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Menu & inventory",
    description:
      "Customers ask about items, prices, and availability in real time"
  },
  {
    title: "Order lookup",
    description: "Customers check their order status by order number instantly"
  },
  {
    title: "Customer profiles",
    description: "Look up customer history, contact info, and purchase patterns"
  },
  {
    title: "Store information",
    description: "Hours, address, phone, and contact info always current"
  },
  {
    title: "Category browsing",
    description: "Browse menu sections and item categories by voice or chat"
  }
];

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  {
    name: "clover_get_merchant_info",
    label: "Get store info",
    group: "Store Info",
    description: "Store name, address, phone, and website from Clover."
  },
  {
    name: "clover_get_hours",
    label: "Get hours",
    group: "Store Info",
    description: "Current operating hours for all days of the week."
  },
  {
    name: "clover_get_categories",
    label: "Get categories",
    group: "Menu & Inventory",
    description: "All item categories — menu sections or product departments."
  },
  {
    name: "clover_search_items",
    label: "Search items",
    group: "Menu & Inventory",
    description:
      "Search items by name or keyword. Returns price, description, and availability."
  },
  {
    name: "clover_get_item",
    label: "Get item details",
    group: "Menu & Inventory",
    description:
      "Full details for a specific item including price and description."
  },
  {
    name: "clover_check_item_availability",
    label: "Check availability",
    group: "Menu & Inventory",
    description: "Check whether a specific item is currently available."
  },
  {
    name: "clover_get_order",
    label: "Get order",
    group: "Orders",
    description: "Full order details by order ID — items, status, and total."
  },
  {
    name: "clover_get_recent_orders",
    label: "Get recent orders",
    group: "Orders",
    description: "The most recent orders with status and totals."
  },
  {
    name: "clover_get_customer",
    label: "Get customer",
    group: "Customers",
    description: "Customer profile by ID — name, email, phone."
  },
  {
    name: "clover_search_customers",
    label: "Search customers",
    group: "Customers",
    description: "Find customers by name, email, or phone number."
  },
  {
    name: "clover_get_modifiers",
    label: "Get modifiers",
    group: "Menu & Inventory",
    description:
      "All modifier groups and options for an item — size, toppings, extras."
  },
  {
    name: "clover_get_discounts",
    label: "Get discounts",
    group: "Store Info",
    description:
      "Active discounts and promotions currently available at the merchant."
  },
  {
    name: "clover_get_employees",
    label: "Get employees",
    group: "Store Info",
    description: "Employee list — names and roles on shift."
  },
  {
    name: "clover_get_tags",
    label: "Get tags",
    group: "Menu & Inventory",
    description:
      "All item tags — dietary labels, allergen flags, and custom attributes."
  },
  {
    name: "clover_get_items_by_tag",
    label: "Get items by tag",
    group: "Menu & Inventory",
    description:
      "Find all items with a specific tag, e.g. 'vegan', 'gluten-free'."
  },
  {
    name: "clover_get_payment_methods",
    label: "Get payment methods",
    group: "Store Info",
    description: "Accepted payment methods at this location."
  }
];

// ─── Shared UI Components ─────────────────────────────────────────────────────

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  return (
    <SectionCard className="mb-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-zinc-50 rounded-2xl"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-zinc-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">
            {CAPABILITIES.length} feature areas
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-zinc-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>
      {open && (
        <div className="px-6 pb-6">
          <Divider />
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
            <strong>AI tools work without any configuration.</strong> Connect
            Clover (Merchant ID and Access Token) and the AI gains access to
            items, orders, customers, and store data in real time.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CAPABILITIES.map((cap, i) => (
              <div
                key={i}
                className="rounded-lg border border-zinc-100 bg-zinc-50 p-4 flex items-start gap-3"
              >
                <div
                  className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                  style={{ backgroundColor: CLOVER_COLOR }}
                />
                <div>
                  <div className="text-sm font-medium text-zinc-700 mb-0.5">
                    {cap.title}
                  </div>
                  <div className="text-xs text-zinc-500">{cap.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function HowItWorksCard() {
  return (
    <SectionCard
      title="How Clover works in Velaro"
      description="Connect once — every item, order, and customer record becomes AI-searchable instantly."
      className="mb-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0"
              style={{ backgroundColor: CLOVER_COLOR }}
            >
              1
            </span>
            <span className="font-semibold text-zinc-700 text-sm">
              Connect Clover
            </span>
          </div>
          <p className="text-xs text-zinc-500 mb-3">
            Click "Connect with Clover" to authorize via OAuth — you'll be
            redirected to Clover and brought back automatically. Or use a
            Personal Access Token from Clover Developer Tools.
          </p>
          <div className="flex items-center gap-2 text-xs text-zinc-600">
            <span className="font-mono bg-white border border-zinc-200 px-1.5 py-0.5 rounded text-[10px]">
              OAuth 2.0 (recommended)
            </span>
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path
                d="M3 2L7 5L3 8"
                stroke="#94a3b8"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
              2
            </span>
            <span className="font-semibold text-green-800 text-sm">
              10 AI tools activate
            </span>
          </div>
          <p className="text-xs text-green-700 mb-3">
            Every item, order, and customer is instantly searchable — no extra
            setup.
          </p>
          <div className="grid grid-cols-2 gap-1">
            {[
              "Item search",
              "Category browse",
              "Order status",
              "Customer lookup",
              "Store info",
              "Hours",
              "Item availability",
              "Recent orders"
            ].map((t) => (
              <div
                key={t}
                className="flex items-center gap-1 text-[10px] text-green-700"
              >
                <svg
                  viewBox="0 0 8 8"
                  width="8"
                  height="8"
                  fill="none"
                  className="shrink-0"
                >
                  <path
                    d="M1 4L3 6L7 2"
                    stroke="#16a34a"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                {t}
              </div>
            ))}
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path
                d="M3 2L7 5L3 8"
                stroke="#94a3b8"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-(--ds-primary) text-white text-xs font-bold flex items-center justify-center shrink-0">
              3
            </span>
            <span className="font-semibold text-blue-800 text-sm">
              Scale 24/7 customer support
            </span>
          </div>
          <p className="text-xs text-blue-700 mb-3">
            Answer item questions, order status, and store hours instantly —
            without tying up staff.
          </p>
          <div className="space-y-2">
            {[
              [
                "Inventory Q&A bot",
                "Answers item, price, and availability questions 24/7"
              ],
              ["Order tracker", "Customers check status without calling"],
              ["Store concierge", "Hours, location, and contact info on demand"]
            ].map(([title, desc]) => (
              <Tile key={title} density="compact" className="!items-start !border-blue-100">
                <div>
                  <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">
                    {title}
                  </div>
                  <div className="text-[10px] text-blue-600 mt-0.5">{desc}</div>
                </div>
              </Tile>
            ))}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

function OverviewTab({
  hasConnection,
  onConnect,
  onOpenWizard
}: {
  hasConnection: boolean;
  onConnect(): void;
  onOpenWizard(): void;
}) {
  return (
    <div>
      <HowItWorksCard />
      {!hasConnection ? (
        <SectionCard className="mb-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-semibold text-zinc-800 mb-1">
                Connect your Clover account
              </div>
              <p className="text-sm text-zinc-500">
                Enter your Merchant ID and Access Token and your items, orders,
                and customer data become instantly searchable by the AI during
                any conversation.
              </p>
            </div>
            <PrimaryButton
              onClick={onConnect}
              className="shrink-0"
              style={{ backgroundColor: CLOVER_COLOR }}
            >
              Connect with Clover
            </PrimaryButton>
          </div>
        </SectionCard>
      ) : (
        <SectionCard className="mb-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-semibold text-zinc-800 mb-1">
                Launch Starter Workflow Wizard
              </div>
              <p className="text-sm text-zinc-500">
                Build an Inventory Q&A Bot, Order Status Bot, or Store Concierge
                in minutes using a pre-built Clover template.
              </p>
            </div>
            <PrimaryButton
              onClick={onOpenWizard}
              className="shrink-0"
              style={{ backgroundColor: CLOVER_COLOR }}
            >
              Launch Wizard
            </PrimaryButton>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

function AiToolsTab({
  hasConnection,
  onOpenWizard
}: {
  hasConnection: boolean;
  onOpenWizard(): void;
}) {
  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];
  return (
    <SectionCard>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-semibold text-zinc-800 text-lg flex items-center gap-2">
            AI Tools
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasConnection ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
            >
              {hasConnection ? `${AI_TOOLS.length} active` : "No connection"}
            </span>
          </div>
          <p className="text-sm text-zinc-500 mt-1">
            All tools activate automatically on connect — no configuration
            required.
          </p>
        </div>
        {hasConnection && <LaunchWizardButton onClick={onOpenWizard} />}
      </div>
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group}>
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
              {group}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <div
                  key={tool.name}
                  className="rounded border border-zinc-100 p-3"
                >
                  <div className="flex items-start gap-2">
                    <div
                      className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                      style={{ backgroundColor: CLOVER_COLOR }}
                    />
                    <div>
                      <div className="text-xs font-semibold text-zinc-700">
                        {tool.label}
                      </div>
                      <div className="text-[10px] font-mono text-zinc-400 mb-1">
                        {tool.name}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {tool.description}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function AccountsTab({
  configs,
  onRefresh,
  onAdd
}: {
  configs: CloverConfig[];
  onRefresh(): void;
  onAdd(): void;
}) {
  const api = useApi();
  const [deleting, setDeleting] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);

  async function handleDelete(id: number) {
    setDeleting(id);
    setPendingDelete(null);
    try {
      await api.admin.delete(`Clover/Configs/${id}`);
      onRefresh();
    } catch {
      // graceful — keep showing existing configs
    } finally {
      setDeleting(null);
    }
  }

  return (
    <SectionCard title="Connected Accounts">
      <div className="flex items-center justify-between mb-4">
        <div></div>
        <PrimaryButton
          onClick={onAdd}
          className="!px-3 !py-1.5 !text-xs"
          style={{ backgroundColor: CLOVER_COLOR }}
        >
          + Add Account
        </PrimaryButton>
      </div>
      {configs.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          <div className="font-medium text-zinc-600 mb-1">
            No Clover account connected
          </div>
          <p className="text-sm">
            Connect your Clover Merchant ID and Access Token to enable
            AI-powered customer support for your store.
          </p>
          <PrimaryButton
            onClick={onAdd}
            className="mt-4"
            style={{ backgroundColor: CLOVER_COLOR }}
          >
            Connect Clover
          </PrimaryButton>
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map((c) => (
            <div
              key={c.id}
              className="rounded-xl border border-zinc-200 p-4 flex items-center justify-between gap-4"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg font-bold"
                  style={{ backgroundColor: CLOVER_COLOR }}
                >
                  C
                </div>
                <div>
                  <div className="font-medium text-zinc-800">
                    {c.displayName}
                  </div>
                  {c.locationName && (
                    <div className="text-xs text-zinc-500">
                      {c.locationName}
                    </div>
                  )}
                  <div className="text-xs text-zinc-400 font-mono">
                    MID: ****{c.merchantId?.slice(-6)}
                  </div>
                  <div className="text-xs text-zinc-400">
                    {new Date(c.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {c.authMethod === "oauth" ? (
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">
                    OAuth
                  </span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-zinc-100 text-zinc-500">
                    Token
                  </span>
                )}
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.enabled ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
                >
                  {c.enabled ? "Active" : "Disabled"}
                </span>
                {pendingDelete === c.id ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-500">
                      Remove this connection?
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDelete(c.id)}
                      disabled={deleting === c.id}
                      className="text-xs text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded transition-colors"
                    >
                      {deleting === c.id ? "Removing..." : "Yes, remove"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingDelete(null)}
                      className="text-xs text-zinc-500 hover:text-zinc-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPendingDelete(c.id)}
                    disabled={deleting === c.id}
                    className="text-xs text-red-500 hover:text-red-700 transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ─── Create Form (OAuth-first) ────────────────────────────────────────────────

interface LegacyFormState {
  displayName: string;
  merchantId: string;
  accessToken: string;
  locationName: string;
}

function CreateForm({
  onSaved,
  onCancel
}: {
  onSaved(): void;
  onCancel(): void;
}) {
  const api = useApi();

  // OAuth flow state
  const [connecting, setConnecting] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);

  // Legacy token flow state
  const [showLegacy, setShowLegacy] = useState(false);
  const [form, setForm] = useState<LegacyFormState>({
    displayName: "",
    merchantId: "",
    accessToken: "",
    locationName: ""
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{
    valid: boolean;
    locationName?: string;
    error?: string;
  } | null>(null);
  const [legacyError, setLegacyError] = useState<string | null>(null);

  async function handleOAuthConnect() {
    setConnecting(true);
    setOauthError(null);
    try {
      const resp = await api.admin.get("Clover/OAuth/AuthUrl");
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as {
          error?: string;
        };
        setOauthError(
          body?.error ?? "Failed to start OAuth flow. Please try again."
        );
        return;
      }
      const data = (await resp.json()) as { authUrl: string };
      window.location.href = data.authUrl;
    } catch {
      setOauthError("Failed to start OAuth flow. Please try again.");
    } finally {
      setConnecting(false);
    }
  }

  async function handleTest() {
    if (!form.merchantId.trim() || !form.accessToken.trim()) {
      setLegacyError(
        "Merchant ID and Access Token are required to test the connection."
      );
      return;
    }
    setTesting(true);
    setTestResult(null);
    setLegacyError(null);
    try {
      const resp = await api.admin.post("Clover/Test", {
        merchantId: form.merchantId.trim(),
        accessToken: form.accessToken.trim()
      });
      const data = await resp.json();
      setTestResult(data);
      if (data.valid && data.locationName) {
        setForm((f) => ({
          ...f,
          locationName: data.locationName,
          displayName: f.displayName || data.locationName
        }));
      }
    } catch {
      setTestResult({
        valid: false,
        error: "Connection test failed. Check your credentials and try again."
      });
    } finally {
      setTesting(false);
    }
  }

  async function handleLegacySave() {
    if (!form.merchantId.trim() || !form.accessToken.trim()) {
      setLegacyError("Merchant ID and Access Token are required.");
      return;
    }
    setSaving(true);
    setLegacyError(null);
    try {
      const resp = await api.admin.post("Clover/Configs", {
        displayName:
          form.displayName.trim() || form.locationName.trim() || "Clover",
        merchantId: form.merchantId.trim(),
        accessToken: form.accessToken.trim(),
        locationName: form.locationName.trim()
      });
      if (!resp.ok) {
        const data = await resp.json();
        setLegacyError(data?.error ?? "Failed to save configuration.");
        return;
      }
      onSaved();
    } catch {
      setLegacyError("Failed to save configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 max-w-2xl">
      {/* ── Primary: OAuth ── */}
      <SectionCard>
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: CLOVER_COLOR }}
          >
            <span className="text-white text-lg font-bold">C</span>
          </div>
          <div>
            <div className="text-base font-semibold text-zinc-800">
              Connect with Clover
            </div>
            <p className="text-xs text-zinc-500">
              Recommended — secure OAuth 2.0, no token to manage
            </p>
          </div>
        </div>

        <p className="text-sm text-zinc-500 mb-5">
          You'll be redirected to Clover to authorize Velaro, then brought back
          automatically. Clover access tokens are long-lived — no expiry to
          manage.
        </p>

        {oauthError && (
          <p className="text-red-600 text-sm mb-4">{oauthError}</p>
        )}

        <div className="flex items-center justify-end gap-3">
          <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
          {connecting ? (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <LoadingSpinner /> Redirecting to Clover…
            </div>
          ) : (
            <PrimaryButton
              onClick={handleOAuthConnect}
              style={{ backgroundColor: CLOVER_COLOR }}
            >
              Connect with Clover
            </PrimaryButton>
          )}
        </div>
      </SectionCard>

      {/* ── Legacy: Personal Access Token ── */}
      <div className="rounded border border-zinc-200 overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center justify-between px-5 py-3 text-sm text-zinc-400 hover:text-zinc-500 transition"
          onClick={() => setShowLegacy((v) => !v)}
          aria-expanded={showLegacy}
        >
          <span className="flex items-center gap-2">
            <span className="text-xs font-medium bg-zinc-200 text-zinc-500 px-2 py-0.5 rounded">
              Legacy
            </span>
            Personal Access Token
          </span>
          <svg
            className={`w-4 h-4 transition-transform ${showLegacy ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {showLegacy && (
          <div className="px-5 pb-5 space-y-4">
            <Divider />
            <p className="text-xs text-zinc-400 mt-3">
              Personal Access Tokens work but require manual creation in the
              Clover Developer Dashboard. Use OAuth above unless you have a
              specific reason to use a token.
            </p>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Display Name
              </label>
              <input
                type="text"
                className="w-full border border-zinc-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 bg-white"
                placeholder="e.g. Main Street Store"
                value={form.displayName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, displayName: e.target.value }))
                }
              />
              <p className="text-xs text-zinc-400 mt-1">
                Optional — auto-filled from Clover after connection test.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Merchant ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full border border-zinc-200 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-400 bg-white"
                placeholder="e.g. A1B2C3D4E5F6G"
                value={form.merchantId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, merchantId: e.target.value }))
                }
              />
              <p className="text-xs text-zinc-400 mt-1">
                Found in your Clover Dashboard URL or under Account &amp; Setup.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Access Token <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                className="w-full border border-zinc-200 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-400 bg-white"
                placeholder="Clover Personal Access Token"
                value={form.accessToken}
                onChange={(e) =>
                  setForm((f) => ({ ...f, accessToken: e.target.value }))
                }
              />
              <p className="text-xs text-zinc-400 mt-1">
                Generate from Clover Dashboard under Developer Tools &rarr;
                Create Token.
              </p>
            </div>
            {testResult && (
              <div
                className={`rounded-lg p-3 text-xs ${testResult.valid ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-700"}`}
              >
                {testResult.valid ? (
                  <span>
                    Connection verified. Store:{" "}
                    <strong>{testResult.locationName || "Unknown"}</strong>
                  </span>
                ) : (
                  <span>{testResult.error}</span>
                )}
              </div>
            )}
            {legacyError && (
              <div className="rounded-lg p-3 text-xs bg-red-50 border border-red-200 text-red-700">
                {legacyError}
              </div>
            )}
            <div className="flex items-center gap-3 pt-1">
              <SecondaryButton
                onClick={handleTest}
                disabled={testing || saving}
                className="!px-3 !py-1.5 !text-sm"
              >
                {testing ? "Testing..." : "Test Connection"}
              </SecondaryButton>
              <PrimaryButton
                onClick={handleLegacySave}
                disabled={saving || testing}
                className="!px-3 !py-1.5 !text-sm"
                style={{ backgroundColor: CLOVER_COLOR }}
              >
                {saving ? "Saving..." : "Save Token"}
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Clover() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [configs, setConfigs] = useState<CloverConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState<number>(0);
  const [oauthBanner, setOauthBanner] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const { teamId } = useSelectedTeam();

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("Clover/Configs");
      if (resp.status === 402) {
        setEnabled(false);
        return;
      }
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  // Handle OAuth callback redirect params
  useEffect(() => {
    const connected = searchParams.get("connected");
    const error = searchParams.get("error");
    if (connected === "true") {
      setOauthBanner({
        type: "success",
        message: "Clover connected successfully via OAuth."
      });
      setActiveTab("accounts");
      setSearchParams({}, { replace: true });
      fetchConfigs();
    } else if (error) {
      const messages: Record<string, string> = {
        missing_params: "OAuth authorization was incomplete. Please try again.",
        invalid_state:
          "OAuth session expired or was tampered with. Please try again.",
        token_exchange_failed:
          "Could not exchange authorization code for a token. Please try again.",
        server_error: "An unexpected server error occurred. Please try again."
      };
      setOauthBanner({
        type: "error",
        message: messages[error] ?? `OAuth error: ${error}`
      });
      setActiveTab("accounts");
      setSearchParams({}, { replace: true });
    }
  }, []); // run once on mount

  useEffect(() => {
    if (configs.length > 0 && selectedConfigId === 0) {
      setSelectedConfigId(configs.find((c) => c.enabled)?.id ?? configs[0].id);
    }
  }, [configs, selectedConfigId]);

  if (!enabled) {
    return (
      <IntegrationNotEnabled
        name="Clover POS Integration"
        description="Clover POS integration requires an upgrade. Contact Velaro support to enable it for your account."
      />
    );
  }

  const hasActiveConfig = configs.some((c) => c.enabled);

  function handleAdd() {
    setShowCreate(true);
    setActiveTab("accounts");
  }

  function handleSaved() {
    setShowCreate(false);
    fetchConfigs();
  }

  const tabsJSX = (
    <>
      <IntegrationTab
        active={activeTab === "overview"}
        onClick={() => {
          setActiveTab("overview");
          setShowCreate(false);
        }}
      >
        Overview
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "accounts"}
        onClick={() => {
          setActiveTab("accounts");
          setShowCreate(false);
        }}
      >
        Accounts
        {configs.length > 0 && (
          <span className="text-xs bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full">
            {configs.length}
          </span>
        )}
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "ai-tools"}
        onClick={() => {
          setActiveTab("ai-tools");
          setShowCreate(false);
        }}
      >
        AI Tools
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "data-mapping"}
        onClick={() => {
          setActiveTab("data-mapping");
          setShowCreate(false);
        }}
      >
        Field Mapping
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "logs"}
        onClick={() => {
          setActiveTab("logs");
          setShowCreate(false);
        }}
      >
        Activity Log
      </IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="Clover" tabs={tabsJSX}>
      {showWizard && (
        <StarterWorkflowWizard
          integration="clover"
          onClose={() => setShowWizard(false)}
        />
      )}
      {oauthBanner && (
        <div
          className={`rounded-xl px-5 py-3 mb-4 flex items-center justify-between gap-4 text-sm font-medium ${oauthBanner.type === "success" ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-700"}`}
        >
          <span>{oauthBanner.message}</span>
          <GhostButton
            onClick={() => setOauthBanner(null)}
            className="!text-current !text-xs !no-underline hover:!no-underline opacity-60 hover:!opacity-100"
          >
            Dismiss
          </GhostButton>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="space-y-4">
          {activeTab === "overview" && (
            <>
              <CapabilitiesPanel />
              <OverviewTab
                hasConnection={hasActiveConfig}
                onConnect={handleAdd}
                onOpenWizard={() => setShowWizard(true)}
              />
            </>
          )}
          {activeTab === "accounts" && (
            <>
              {hasActiveConfig && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="text-sm font-medium text-blue-800 mb-0.5">
                        Deploy the chat widget to your site
                      </div>
                      <div className="text-xs text-blue-600">
                        Your Clover integration is connected — grab your embed
                        code from Deployments to go live.
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Link
                        to="/chat_window"
                        className="text-xs px-3 py-1.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-100 transition"
                      >
                        Chat Window
                      </Link>
                      <Link
                        to="/deployment"
                        className="text-xs px-3 py-1.5 rounded-lg bg-(--ds-primary) hover:opacity-90 text-white transition"
                      >
                        Get Embed Code
                      </Link>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
                    <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      <strong>Tip:</strong> Use <strong>Page Widgets</strong>{" "}
                      from{" "}
                      <Link
                        to="/deployment"
                        className="underline hover:text-blue-900"
                      >
                        Deployments &rarr; Page Widgets
                      </Link>{" "}
                      to add a lightweight item Q&A widget on your ordering page
                      — no live agent required.
                    </span>
                  </div>
                </div>
              )}
              {showCreate ? (
                <CreateForm
                  onSaved={handleSaved}
                  onCancel={() => setShowCreate(false)}
                />
              ) : (
                <AccountsTab
                  configs={configs}
                  onRefresh={fetchConfigs}
                  onAdd={handleAdd}
                />
              )}
            </>
          )}
          {activeTab === "ai-tools" && (
            <AiToolsTab
              hasConnection={hasActiveConfig}
              onOpenWizard={() => setShowWizard(true)}
            />
          )}
          {activeTab === "data-mapping" && (
            <SectionCard title="Field Mapping — Clover">
              <CloverDataMapping
                teamId={teamId ?? 0}
                selectedConfigId={selectedConfigId}
              />
            </SectionCard>
          )}
          {activeTab === "logs" && (
            <IntegrationActivityLog integrationSlug="Clover" />
          )}
        </div>
      )}
    </StickyRouteHeader>
  );
}
